import Zone from "@frog/source/models/Zone.js"
import Dev from "@frog/source/models/admin/Dev.js"
import Tilesetter from "@frog/source/models/admin/Tilesetter.js"

import Random from "@frog/source/utility/Random.js"
import {nanoid} from "nanoid"
import Objdict from "objdict"

import basicGame from "@frog/source/data/basicGame.js"
import basicParty from "@frog/source/data/basicParty.js"
import basicChapter from "@frog/source/data/basicChapter.js"

const CHAPTER_REQUIRE_STRING = `
        "CHAPTERNAME": require("./CHAPTERNAME").default,`

function cleanMarkdownString(string) {
    string = string.trim()
    if(string.startsWith("*") && string.endsWith("*")) {
        string = string.replaceAll("*","")
        string = string.trim()
    }
    if(string.startsWith("_") && string.endsWith("_")) {
        string = string.replaceAll("_","")
        string = string.trim()
    }
    return string
}
function cleanStringIntoFilename(string) {
    string = string.toLowerCase()
    string = string.replaceAll(" ","_")
    string = string.replaceAll(/[~"#%&*:<>?/\\{|}]+/gi,"")
    return string
}

export function parseGamescript(text) {
    const lines = text.split("\n")
    let gameName
    let chapters = []
    const parsedLines = []
    const separators = Dev.scriptImportSeparatorStrings
    lines.forEach((line) => {
        let parsedLine = {
            "line": line,
        }
        if(line.trim().length == 0) return
        if(line.startsWith(separators.game) == true) {
            gameName = line.slice(separators.game.length).toLowerCase()
            parsedLine.class = "GameHeader"
        } else if(line.startsWith(separators.chapter) == true) {
            let shortenedName = line.slice(separators.chapter.length)
            shortenedName = shortenedName.replaceAll("Chapter","")
            shortenedName = cleanMarkdownString(shortenedName)

            parsedLine.class = "ChapterHeader"
            chapters.push({
                "key": cleanStringIntoFilename(shortenedName),
                "name": shortenedName,
                "sequenceOrder": chapters.length,
                "encounters": []
            })
        } else if(line.startsWith(separators.encounter) == true) {
            let shortenedName = line.slice(separators.encounter.length)
            shortenedName = shortenedName.replaceAll("Encounter","")
            shortenedName = cleanMarkdownString(shortenedName)

            parsedLine.class = "EncounterHeader"
            if(chapters[chapters.length - 1] == undefined) {
                parsedLine.isUncaught = true
            } else {
                chapters[chapters.length - 1].encounters.push({
                    "key": cleanStringIntoFilename(shortenedName),
                    "name": shortenedName,
                    "sequenceOrder": chapters[chapters.length - 1].encounters.length,
                    "conditions": [{
                        "comment": "start",
                        "lines": []
                    }]
                })
            }
        } else if(line.startsWith(separators.condition) == true) {
            const currentChapter = chapters[chapters.length - 1]

            parsedLine.class = "ConditionHeader"
            if(currentChapter?.encounters[currentChapter?.encounters.length - 1] == undefined) {
                parsedLine.isUncaught = true
            } else {
                currentChapter?.encounters[currentChapter?.encounters.length - 1]?.conditions.push({
                    "comment": line.slice(separators.condition.length),
                    "lines": []
                })
            }
        } else {
            const currentChapter = chapters[chapters.length - 1]
            const currentEncounter = currentChapter?.encounters[currentChapter.encounters.length - 1]
            const currentCondition = currentEncounter?.conditions[currentEncounter.conditions.length - 1]

            parsedLine.class = "ParsedDialogue"
            if(currentCondition == undefined) {
                parsedLine.isUncaught = true
            } else {
                currentCondition?.lines.push(line)
            }
        }
        parsedLines.push(parsedLine)
    })

    return {
        "gameName": gameName,
        "chapters": chapters,
        "parsedLines": parsedLines,
    }
}

export async function generateGameBoilerplate(parsedGamescript, options) {
    options = options || {}
    const gameName = parsedGamescript.gameName
    const chapters = parsedGamescript.chapters
    let chapterRequiresStrings = ``
    chapters.forEach((chapter) => {
        let nextURL
        if(chapter.sequenceOrder + 1 >= chapters.length) {
            nextURL = `"/credits"`
        } else {
            nextURL = `"/games/` + gameName + `/chapters/` + chapters.find((nextChapter) => nextChapter.sequenceOrder == chapter.sequenceOrder + 1).key + `"`
        }
        generateChapterBoilerplate({
            "newGameSlug": gameName,
            "newChapterSlug": chapter.key,
            "nextURL": nextURL,
            "inputEncounters": chapter.encounters,
            "options": options,
        })
        chapterRequiresStrings += CHAPTER_REQUIRE_STRING.replaceAll("CHAPTERNAME", chapter.key)
    })

    const gameFolder = await history.state.dataFolder.getDirectoryHandle(gameName, {"create": true})
    let gameFilePointer = await gameFolder.getFileHandle("index.js", {"create": true})

    let script = basicGame
    script = script.replace("newGameSlug", gameName)
    script = script.replace("startingChapterKey", chapters[0].key)
    script = script.replace("chapterRequiresStrings", chapterRequiresStrings)
    await writeToFile({
        "file": gameFilePointer,
        "value": script,
        "options": options
    })

    let partyFilePointer = await gameFolder.getFileHandle("player.js", {"create": true})
    let partyJS = basicParty
    partyJS = partyJS.replace("newGameSlug", gameName)
    await writeToFile({
        "file": partyFilePointer,
        "value": partyJS,
        "options": options
    })
}

export async function generateChapterBoilerplate({newGameSlug, newChapterSlug, nextURL, inputEncounters, options}) {
    options = options || {}
    const encounters = inputEncounters.map((encounter) => {
        const width = 4
        const height = 4
        const x = 5 + encounter.sequenceOrder * (width + 1)
        const y = 5
        const z = 1
        const stampPoints = [
            {"x": x, "y": y},
            {"x": x + width, "y": y},
            {"x": x + width, "y": y + height},
            {"x": x, "y": y + height},
        ]
        return {
            "key": encounter.key,
            "slug": encounter.key,
            "zoomout": 1.0,
            "z": z,
            "tiles": [],
            "points": stampPoints.map((point) => {
                return {
                    "x": point.x,
                    "y": point.y,
                    "key": nanoid().slice(0,5),
                }
            })
        }
    })

    const minX = Math.min(...encounters.map((encounter) => Math.min(...encounter.points.map((point) => point.x))))
    const maxX = Math.max(...encounters.map((encounter) => Math.max(...encounter.points.map((point) => point.x))))
    const minY = Math.min(...encounters.map((encounter) => Math.min(...encounter.points.map((point) => point.y))))
    const maxY = Math.max(...encounters.map((encounter) => Math.max(...encounter.points.map((point) => point.y))))

    const world = {
        "width": maxX + 1,
        "height": maxY + 1,
        "zones": encounters,

        "blocks": {},
        "terrain": {},
    }
    for(let scanX = 0; scanX < world.width; scanX += 1) {
        for(let scanY = 0; scanY < world.height; scanY += 1) {
            const position = {"x": scanX, "y": scanY, "z": 1}
            const block = {
                "position": position,
                "tileKey": "14",
            }
            block.position.xy = block.position.x + "x" + block.position.y
            block.position.xyz = block.position.xy + "x" + block.position.z
            world.blocks[block.position.xyz] = block
            world.terrain[block.position.xy] = block
            world.terrain[block.position.xy].size = {"x": 1, "y": 1, "z": 0}
            encounters.forEach((encounter) => {
                if(Zone.isInBounds(encounter, position) == true) {
                    block.tileKey = "sand"
                    encounter.tiles.push(block)
                }
            })
        }
    }


    world.spots = {}
    const units = {}
    encounters.forEach((encounter) => {
        for(let counter = 0; counter < 3; counter++) {
            let randomSpawnTile = Random.element(encounter.tiles)
            while(Object.values(world.spots).find((spot) => {
                return spot.position.x == randomSpawnTile.position.x
                    && spot.position.y == randomSpawnTile.position.y
            }) != undefined) {
                randomSpawnTile = Random.element(encounter.tiles)
            }
            const newKey = nanoid().slice(0,5)
            world.spots[newKey] = {
                "key": newKey,
                "tag": "spawn",
                "position": randomSpawnTile.position,
            }
        }

        for(let counter = 0; counter < 3; counter++) {
            let randomEnemyTile = Random.element(encounter.tiles)
            while(Object.values(world.spots).find((spot) => {
                return spot.position.x == randomEnemyTile.position.x
                    && spot.position.y == randomEnemyTile.position.y
            }) != undefined || Object.values(units).find((unit) => {
                return unit.position.x == randomEnemyTile.position.x
                    && unit.position.y == randomEnemyTile.position.y
            }) != undefined) {
                randomEnemyTile = Random.element(encounter.tiles)
            }
            const newKey = nanoid().slice(0,5)
            units[newKey] = {
                "key": newKey,
                "position": randomEnemyTile.position,
                "randomJobKey": ["sword", "spear", "hammer","dagger", "bow"].join(","),
                "affiliation": "bad",
            }
        }
    })

    Dev.loadedProtostage = {
        "units": units,
        "world": world,
    }

    let conditionsText = ``
    inputEncounters.forEach((encounter) => {
        encounter.conditions.forEach((condition) => {
            if(condition.comment == "start") {
                const precedingEncounter = inputEncounters.find((candidate) => candidate.sequenceOrder == encounter.sequenceOrder - 1)
                if(precedingEncounter == undefined) {
                    conditionsText += `
            {
                // Starting the level
                "type": "turn:0:start",
                "events": [
                    PlotSteps.SpawnPlayerPartyScript({"spotTag": "spawn"}),`
                } else {
                    conditionsText += `
            {
                // Moving to next encounter
                "type": "condition",
                "isWinCondition": true,
                "condition": (stage) => {
                    return stage.currentEncounterKey == "` + precedingEncounter.key + `"
                        && PlotConditions.areAllUnaffiliatedUnitsDefeated(stage, {"affiliation": stage.player.affiliation, "encounterKey": "` + precedingEncounter.key + `"})
                },
                "events": [
                    PlotSteps.MoveEncounterScript({"nextEncounterKey": "` + encounter.key + `", "spotTag": "spawn"}),`
                }
            } else {
                conditionsText += `
            {
                // Placeholder
                "type": "placeholder",
                "events": [`
            }
            condition.lines.forEach((line) => {
                if(line.startsWith(Dev.scriptImportSeparatorStrings.comment)) {
                    conditionsText += `
                    ` + line
                    return
                }
                if(line.startsWith("~~")) return
                conditionsText += `
                    "` + line + `",`
            })
            conditionsText += `
                ]
            },`
        })
    })

    const chapterOptions = Objdict.merge(options, {
        "gameslug": newGameSlug,
        "chapterslug": newChapterSlug,
    })
    Dev.saveUnitsToFile(chapterOptions)
    Dev.saveEncountersToFile(chapterOptions)
    Dev.saveTilesToFile(chapterOptions)
    Dev.saveSpotsToFile(chapterOptions)

    const gameFolder = await history.state.dataFolder.getDirectoryHandle(newGameSlug, {"create": true})
    const chapterFolder = await gameFolder.getDirectoryHandle(newChapterSlug, {"create": true})

    let script = basicChapter
    script = script.replaceAll("chapterNameSlug", `"` + newChapterSlug + `"`)
    script = script.replaceAll("scriptedConditions", conditionsText)
    script = script.replaceAll("linkToNextChapterURLString", nextURL)
    script = script.replaceAll("lastEncounterKey", inputEncounters[inputEncounters.length - 1].key)

    const scriptFile = await chapterFolder.getFileHandle("index.js", {"create": true})
    await writeToFile({
        "file": scriptFile,
        "value": script,
        "options": options
    })

    const backgroundFile = await chapterFolder.getFileHandle("tiles.png", {"create": true})
    await writeToFile({
        "file": backgroundFile,
        "value": convertDataURLtoPNG(Tilesetter.drawEnvironment({world})[0]),
        "options": options
    })
}

async function writeToFile({file, value, options}) {
    if(options.overwriteExisting != true) {
        const readable = await file.getFile()
        const text = await readable.text()

        if(text.length > 0) return
    }

    const writable = await file.createWritable()
    await writable.write(value)
    await writable.close()
}

function convertDataURLtoPNG(dataURL) {
    const parts = dataURL.split(",")
    const binaryString = atob(parts[parts.length - 1])
    u8arr = new Uint8Array(binaryString.length)
    for(let counter = binaryString.length; counter >= 0 ; counter--) {
        u8arr[counter] = binaryString.charCodeAt(counter)
    }
    return u8arr
}
